// src/components/HelpModal/index.tsx
import { FC, useState, useRef, ChangeEvent, useEffect } from 'react';
import { Modal, Input, Button, App } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { submitHelpRequest, getHelpAttachmentUploadUrl } from '@/apis/help';
import { uploadToS3 } from '@/apis/aws';
import ContactSuccessModal from '@/pages/ContactUs/components/ContactSuccessModal';
import ContactFailModal from '@/pages/ContactUs/components/ContactFailModal';
import useThemeStore from '@/store/useThemeStore';
import { getFileExtension, getFileSizeKb, trackEvent } from '@/utils/analytics';
import { EVENTS } from '@/utils/analyticsEvents';
import discordIcon from '@/assets/svg/discord.svg';
import styles from './index.module.scss';
import SvgIcon from '../SvgIcon';

const { TextArea } = Input;

interface FileItem {
  id: string;
  name: string;
  file: File;
}

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

const HelpModal: FC<HelpModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isDarkMode } = useThemeStore();
  const location = useLocation();
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 根据主题选择 logo
  const mobileLogo = isDarkMode ? '/logo/THEPATCH-D.png' : '/logo/THEPATCH-L.png';
  const mobileLogoSrcSet = isDarkMode ? '/logo/THEPATCH-D.png 1x, /logo/THEPATCH-D@2x.png 2x' : '/logo/THEPATCH-L.png 1x, /logo/THEPATCH-L@2x.png 2x';

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const handleAttachImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    const newFiles: FileItem[] = [];
    const maxFiles = 1; // 最多 1 个文件，与 ContactUs 保持一致
    const maxSize = 10 * 1024 * 1024; // 10MB

    for (let i = 0; i < selectedFiles.length; i++) {
      if (files.length + newFiles.length >= maxFiles) {
        message.warning(t('help.maxFiles'));
        break;
      }

      const file = selectedFiles[i];
      if (!file) continue;

      if (file.size > maxSize) {
        message.warning(t('help.fileSizeLimit'));
        trackEvent(EVENTS.FILE_UPLOAD_FAILED, {
          file_type: getFileExtension(file.name),
          file_size_kb: getFileSizeKb(file),
          error_reason: 'size_exceeded',
          feature: 'help',
        });
        continue;
      }

      newFiles.push({
        id: `${Date.now()}-${i}`,
        name: file.name,
        file,
      });
    }

    setFiles([...files, ...newFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (id: string) => {
    setFiles(files.filter((f) => f.id !== id));
  };

  const handleConfirm = async () => {
    // 1. 验证
    if (!description.trim()) {
      message.warning(t('help.descriptionRequired'));
      return;
    }

    setLoading(true);

    try {
      // 2. 上传文件到 S3（如果有，最多1个）
      let imageUrl: string | null = null;

      if (files.length > 0) {
        const file = files[0]; // 只取第一个文件

        if (!file) {
          throw new Error('File is undefined');
        }

        // 获取预签名 URL
        const uploadUrlRes = await getHelpAttachmentUploadUrl({
          file_name: file.file.name,
          file_size: file.file.size,
        });

        const uploadData = uploadUrlRes.data.data;
        if (!uploadData?.presigned_url || !uploadData?.file_key) {
          throw new Error('Failed to get presigned upload URL');
        }

        // 上传到 S3
        const s3Response = await uploadToS3(uploadData.presigned_url, file.file);
        if (!s3Response.ok) {
          trackEvent(EVENTS.FILE_UPLOAD_FAILED, {
            file_type: getFileExtension(file.file.name),
            file_size_kb: getFileSizeKb(file.file),
            error_reason: 'network_error',
            feature: 'help',
          });
          throw new Error('Failed to upload image to S3');
        }

        imageUrl = uploadData.file_key;
      }

      // 3. 提交 Help 请求
      await submitHelpRequest({
        description: description.trim(),
        image_url: imageUrl,
      });

      // 4. 成功处理
      setLoading(false);
      trackEvent(EVENTS.HELP_MESSAGE_SUBMITTED, {
        page: location.pathname,
        has_attachment: files.length > 0,
        message_length: description.trim().length,
      });
      setShowSuccessModal(true);
      handleClose(); // 关闭 HelpModal 并重置表单
    } catch (error) {
      // 5. 失败处理
      setLoading(false);
      setShowFailModal(true);
      console.error('Failed to submit help request:', error);
    }
  };

  const handleClose = () => {
    setDescription('');
    setFiles([]);
    onClose();
  };

  const handleSuccessModalConfirm = () => {
    setShowSuccessModal(false);
  };

  const handleFailModalRetry = () => {
    setShowFailModal(false);
    handleConfirm(); // 重新提交
  };

  const handleFailModalCancel = () => {
    setShowFailModal(false);
  };

  const closeIcon = (
    <svg className={styles.closeIcon} aria-hidden="true">
      <use xlinkHref="#icon-x-black" />
    </svg>
  );

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        footer={null}
        closeIcon={isMobile ? null : closeIcon}
        className={styles.helpModal}
        wrapClassName={isMobile ? styles.helpModalWrap : undefined}
        width={isMobile ? '100%' : 582}
        centered={!isMobile}
        styles={isMobile ? ({ content: { borderRadius: 0, padding: 0 }, body: { padding: 0 } } as never) : undefined}
      >
        {/* 移动端自定义顶部区域 */}
        {isMobile && (
          <div className={styles.mobileHeader}>
            <img src={mobileLogo} srcSet={mobileLogoSrcSet} alt="The Patch" className={styles.mobileLogo} />
            <button className={styles.mobileCloseBtn} onClick={handleClose} type="button" aria-label="Close">
              <SvgIcon name="x-black" size={24} />
            </button>
          </div>
        )}
        <div className={styles.modalContent}>
          <div className={styles.header}>
            <h2 className={styles.title}>{t('help.title')}</h2>
            <p className={styles.subtitle}>{t('help.subtitle')}</p>
          </div>

          <div className={styles.formContent}>
            <TextArea className={styles.textarea} placeholder={t('help.descriptionPlaceholder')} value={description} onChange={(e) => setDescription(e.target.value)} style={{ resize: 'none' }} />

            {files.length > 0 && (
              <div className={styles.fileList}>
                {files.map((file) => (
                  <div key={file.id} className={styles.fileItem}>
                    <div className={styles.fileIcon}>
                      <SvgIcon name="Attachment" path="contact_us" size={20} />
                    </div>
                    <span className={styles.fileName}>{file.name}</span>
                    <button className={styles.deleteButton} onClick={() => handleRemoveFile(file.id)} type="button" aria-label="Delete file">
                      <SvgIcon name="trash-bold 1" path="contact_us" size={25} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <Button size="large" className={`btn-secondary ${styles.attachButton}`} onClick={handleAttachImage}>
              {t('help.attachImage')}
              <SvgIcon name="Attachment" size={25} />
            </Button>
            <Button type="primary" size="large" onClick={handleConfirm} loading={loading} disabled={loading}>
              {t('help.confirm')}
            </Button>
          </div>

          {/* Discord Community Link */}
          <a href="https://discord.gg/FpYyghTX2p" target="_blank" rel="noopener noreferrer" className={styles.discordLink}>
            <div className={styles.discordIconWrapper}>
              <img src={discordIcon} alt="Discord" className={styles.discordIcon} />
            </div>
            <div className={styles.discordContent}>
              <span className={styles.discordTitle}>{t('help.discordTitle')}</span>
              <span className={styles.discordSubtitle}>{t('help.discordSubtitle')}</span>
            </div>
            <RightOutlined className={styles.discordArrow} />
          </a>

          <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFileChange} />
        </div>
      </Modal>

      {/* Success Modal - 复用 ContactUs 的组件 */}
      <ContactSuccessModal open={showSuccessModal} onCancel={handleSuccessModalConfirm} onConfirm={handleSuccessModalConfirm} />

      {/* Fail Modal - 复用 ContactUs 的组件 */}
      <ContactFailModal open={showFailModal} onCancel={handleFailModalCancel} onRetry={handleFailModalRetry} />
    </>
  );
};

export default HelpModal;
